import { useEffect, useState } from 'react';

/**
 * F-301 — one-time welcome card for new volunteers.
 *
 * Renders on the Dashboard for any user that has never dismissed it
 * (tracked via localStorage). Lightweight, no interruption -- just a
 * "three things to try first" card with links to the Guide, the
 * keyboard shortcut help, and the pending queue. Dismiss persists.
 */
const KEY_PREFIX = 'moderation-ui:onboarding-dismissed:';

function readDismissed(userKey: string): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(KEY_PREFIX + userKey) === '1';
  } catch {
    return true;
  }
}

function setDismissed(userKey: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEY_PREFIX + userKey, '1');
  } catch {
    // ignore
  }
}

interface OnboardingCardProps {
  /** Stable identifier for the current user (Cognito sub or email). */
  userKey: string;
  onNavigate: (hash: string) => void;
}

export default function OnboardingCard({ userKey, onNavigate }: OnboardingCardProps) {
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    setHidden(readDismissed(userKey));
  }, [userKey]);

  if (hidden) return null;

  const dismiss = () => {
    setHidden(true);
    setDismissed(userKey);
  };

  return (
    <aside
      className="onboarding-card"
      role="region"
      aria-label="Welcome — three things to try first"
      data-testid="onboarding-card"
    >
      <header className="onboarding-card__header">
        <h2>Welcome — three things to try first</h2>
        <button
          type="button"
          className="onboarding-card__dismiss"
          onClick={dismiss}
          aria-label="Dismiss welcome card"
          data-testid="onboarding-dismiss"
        >
          ×
        </button>
      </header>
      <ol className="onboarding-card__list">
        <li>
          Read the{' '}
          <a
            href="#/guide"
            onClick={(e) => {
              e.preventDefault();
              onNavigate('#/guide');
            }}
          >
            moderator guide
          </a>{' '}
          for what to look for in each submission.
        </li>
        <li>
          Press <kbd>?</kbd> any time to see keyboard shortcuts. <kbd>j</kbd>
          {' / '}
          <kbd>k</kbd> step through submissions, <kbd>a</kbd>/<kbd>r</kbd>
          {' '}approve / reject.
        </li>
        <li>
          Open the{' '}
          <a
            href="#/submissions?status=pending_review"
            onClick={(e) => {
              e.preventDefault();
              onNavigate('#/submissions?status=pending_review');
            }}
          >
            pending queue
          </a>{' '}
          and pick your first submission.
        </li>
      </ol>
    </aside>
  );
}
